import React, { useEffect, useMemo } from 'react';

import Box from '@mui/material/Box';

import UserProfileModalHeader from './UserProfileModalHeader';
import ParticipantProfilePreview from './ParticipantProfilePreview';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';

import ParticipantScopes from '@constants/scopes';
import {
  getIsAdminParticipantBusy,
  getIsParticipantBusy,
} from '../../helpers/followMe';

import AppModal from '../AppModal';

import { IUserProfileModal } from './interfaces';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';

import { useStyles } from './styles';
import { useOverlayContext } from '@providers/OverlayProvider';

const ParticipantProfileModal = ({
  onClose,
  isOpen,
  previewingParticipant = null,
}: IUserProfileModal) => {
  const classes = useStyles();

  const { sendMessageToEvent, user } = useMytaverse();
  const {
    currentEvent,
    pendingFollowersData,
    acceptedFollowersData,
    currentParticipant,
    participants,
  } = useMytaverseEvent();

  const { setHideCurrentlyOpenOverlays } = useOverlayContext();

  const previewingParticipantData = useMemo(
    () =>
      participants.find(
        (participant) =>
          participant.participantId === previewingParticipant?.participantId,
      ),
    [participants, previewingParticipant?.participantId],
  );
  const isParticipantOnline = !!previewingParticipantData?.roomId;

  const getShowFollowMe = (): boolean => {
    const isCurrentParticipant =
      previewingParticipantData?.participantId ===
      currentParticipant?.participantId;

    if (isCurrentParticipant) {
      return false;
    }

    const followersData = pendingFollowersData.concat(acceptedFollowersData);

    const isBusyCurrentParticipant = getIsParticipantBusy(
      followersData,
      currentParticipant?.participantId || '',
    );

    if (isBusyCurrentParticipant) {
      return false;
    }

    const previewingParticipantId = previewingParticipant?.participantId || '';
    const previewingParticipantScopes = user.roleScopes?.find(
      (r) => r.roleName === previewingParticipantData?.eventRole,
    )?.permissions;
    const isPreviewingParticipantAdmin =
      previewingParticipantScopes?.includes(
        ParticipantScopes.HubAdminFollowMe,
      ) ?? false;

    if (isPreviewingParticipantAdmin) {
      const isAdminPreviewingParticipantBusy = getIsAdminParticipantBusy(
        followersData,
        previewingParticipantId,
      );

      return !isAdminPreviewingParticipantBusy;
    }

    const isGuestPreviewingParticipantBusy = getIsParticipantBusy(
      followersData,
      previewingParticipantId,
    );

    return !isGuestPreviewingParticipantBusy;
  };

  const handleFollow = () => {
    sendMessageToEvent(currentEvent?.eventId || '', {
      action: WebsocketAction.FollowPlayer,
      ownerId: previewingParticipantData?.participantId,
      followerId: currentParticipant?.participantId,
      ownerName: previewingParticipantData?.fullName,
      ownerGameSessionId: previewingParticipantData?.gameSessionId,
    });
    onClose();
  };

  useEffect(() => {
    isOpen
      ? setHideCurrentlyOpenOverlays(true)
      : setHideCurrentlyOpenOverlays(false);

    return () => {
      setHideCurrentlyOpenOverlays(false);
    };
  }, [setHideCurrentlyOpenOverlays, isOpen]);

  return (
    <AppModal isOpen={isOpen} onClick={onClose} sx={classes.container}>
      <Box sx={classes.content}>
        <UserProfileModalHeader onClose={onClose} />
        <Box sx={classes.contentWrapper}>
          <ParticipantProfilePreview
            user={previewingParticipant}
            showFollowMe={isParticipantOnline ? getShowFollowMe() : false}
            onFollow={handleFollow}
          />
        </Box>
      </Box>
    </AppModal>
  );
};

export default ParticipantProfileModal;
